import { Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { UserApiService } from '@app/auth/api/user-api.service';
import { User } from '@app/utils/models/user/user.model';
import { map, Observable, tap } from 'rxjs';
import { MemberUser } from '@app/utils/models/user/member-user.model';
import { TalkJsDomainService } from '@app/chat/domain/talk-js-domain.service';
import { MemberProfileFormValue } from '@app/utils/models/user/member-profile-form-value.model';

@Injectable({
  providedIn: 'root',
})
export class UserDomainService {
  user$$: Signal<User | null>;
  private _user$$: WritableSignal<User | null> = signal(null);

  constructor(
    private userApiService: UserApiService,
    private talkJsDomainService: TalkJsDomainService,
  ) {
    this.user$$ = this._user$$.asReadonly();
  }

  initUserFromStorage$(): Observable<User> {
    return this.userApiService.getUserFromStorage$().pipe(
      tap(user => {
        this.talkJsDomainService.initTalkJs(user);
        this._user$$.set(user);
      }),
    );
  }

  clearCurrentUser(): void {
    this._user$$.set(null);
    this.talkJsDomainService.destroyTalkJsSession();
  }

  /**
   * Local-only: the backend sets `is_onboarding_done` itself on submit. This just stops
   * `membersAreaOnboardingGuard` from sending the member back into the flow before the next
   * `users/me/` refresh.
   */
  markOnboardingCompleted(): void {
    this.patchUser({ hasToShowOnboarding: false });
  }

  // Not authenticated user
  getMemberUserByHash$(hash: string): Observable<MemberUser> {
    return this.userApiService.getMemberUserByHash$(hash);
  }

  // Saves the member's profile and merges the saved fields into `user$$`, so every screen reading it
  // (greeting, request-trip, profile) shows the new values without refetching `users/me/`.
  updateUser$(memberProfileFormValue: MemberProfileFormValue): Observable<void> {
    return this.userApiService.updateUser$(memberProfileFormValue).pipe(
      tap(user => this.patchUser(user)),
      map(() => undefined),
    );
  }

  private patchUser(patch: Partial<User>): void {
    this._user$$.update(user => (user ? { ...user, ...patch } : null));
  }
}
